import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  DatePickerProps,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { rechargeCredits, scheduleCredits, unscheduleCredits, workspaceQueries } from '../../../../../Actions'
import {
  SCHEDULE_STATUS_COLOR,
  SCHEDULE_STATUS_LABEL,
  TransactionSchedule,
  TransactionScheduleStatus,
} from '../../../../../types/credit'

type RechargeType = 'IMMEDIATE' | 'SCHEDULED'

type Props = {
  workspaceId: number
}

const extractErrorMessage = (e: any) =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || '충전에 실패했습니다.'

export default function RechargeCredits({ workspaceId }: Props) {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [rechargeType, setRechargeType] = useState<RechargeType>('IMMEDIATE')
  const [rechargeAmount, setRechargeAmount] = useState('')
  const [scheduleDate, setScheculeDate] = useState<DatePickerProps['value']>()
  const [modalMessage, setModalMessage] = useState('')
  const queryClient = useQueryClient()
  const { mutateAsync: mutateToRechargeCredits } = useMutation({ mutationFn: rechargeCredits })
  const { mutateAsync: mutateToScheduleCredits } = useMutation({ mutationFn: scheduleCredits })
  const { mutate: mutateToUnscheduleCredits } = useMutation({ mutationFn: unscheduleCredits })
  const { data, refetch: refetchScheduledCreditsList } = useQuery({
    // status 미지정 = 4상태 전부. 실행·취소된 예약도 목록에 남는다
    ...workspaceQueries.detail(workspaceId)._ctx.scheduleCredits({ min: 0 }),
  })
  const [isLoading, setIsLoading] = useState(false)

  const showModal = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    setModalMessage('')
  }

  const handleRecharge = async () => {
    if (!rechargeAmount) {
      setModalMessage('크레딧을 입력해주세요.')
      return
    }

    const amount = parseFloat(rechargeAmount)

    if (!(amount > 0)) {
      setModalMessage('충전할 크레딧은 0 보다 커야 합니다. 회수는 회수하기에서 진행합니다.')
      return
    }

    if (rechargeType === 'SCHEDULED') {
      if (!scheduleDate) {
        setModalMessage('예약일시를 적어주세요.')
        return
      }
      // 플랜 부여/회수 예약과 같은 기준. 과거 시각도 여기서 함께 걸린다.
      // 과거 시각은 백엔드가 검증 없이 받아 Quartz 가 즉시 발화시킨다
      if (scheduleDate.isBefore(dayjs().add(5, 'minutes'))) {
        setModalMessage('예약일시를 현재의 5분 이후로 설정해주세요.')
        return
      }
    }

    try {
      setIsLoading(true)

      if (rechargeType === 'SCHEDULED') {
        await mutateToScheduleCredits({
          workspaceId,
          amount,
          memo: '어드민 부여',
          scheduledAt: scheduleDate!.format('YYYY-MM-DDTHH:mm:ss'),
        })
        message.success('충전을 예약했습니다.')
      } else {
        await mutateToRechargeCredits({
          workspaceId,
          amount,
          memo: '어드민 부여',
        })
        message.success(`${amount} 크레딧을 충전했습니다.`)
      }

      setIsModalVisible(false)
      setRechargeAmount('')
      setModalMessage('')
      await queryClient.invalidateQueries({ queryKey: workspaceQueries.detail(workspaceId).queryKey })
    } catch (e) {
      // 400 을 삼키면 어드민이 충전된 줄 알고 화면을 떠난다
      setModalMessage(extractErrorMessage(e))
    } finally {
      setIsLoading(false)
    }
  }

  const handleScheduleChanged = (value: DatePickerProps['value']) => {
    setScheculeDate(value)
  }

  return (
    <>
      <div>
        <Flex justify="flex-start">
          <Button onClick={showModal}>충전하기</Button>
        </Flex>

        <Typography.Title level={5}>충전 예약 내역</Typography.Title>
        <Table
          dataSource={data?.content}
          rowKey={(record: TransactionSchedule) => record.id}
          pagination={false}
          columns={[
            {
              title: 'scheduledAt',
              dataIndex: 'scheduledAt',
              key: 'scheduledAt',
            },
            {
              title: 'amount',
              dataIndex: 'amount',
              key: 'amount',
            },
            {
              title: '상태',
              dataIndex: 'status',
              key: 'status',
              render: (status: TransactionScheduleStatus) =>
                status ? <Tag color={SCHEDULE_STATUS_COLOR[status]}>{SCHEDULE_STATUS_LABEL[status]}</Tag> : '-',
            },
            {
              title: '원장',
              dataIndex: 'transactionId',
              key: 'transactionId',
              render: (transactionId: number | null) => transactionId ?? '-',
            },
            {
              title: 'issuerEmail',
              dataIndex: 'issuerEmail',
              key: 'issuerEmail',
              render: (t, r: any) => <Link to={`/user/${r.issuerId}`}>{r.issuerEmail}</Link>,
            },
            {
              width: 100,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              render: (t, r: TransactionSchedule) =>
                // 예약취소는 미실행 건만. 다른 상태는 백엔드가 IllegalStateException 을 던진다
                r.status === 'PENDING' ? (
                  <Popconfirm
                    title="예약취소"
                    description="예약을 취소하시겠습니까?"
                    onConfirm={() => {
                      mutateToUnscheduleCredits(r.id, { onSuccess: () => refetchScheduledCreditsList() })
                    }}
                    okText="예"
                    cancelText="아니오"
                  >
                    <Button>예약취소</Button>
                  </Popconfirm>
                ) : null,
            },
          ]}
          scroll={{ y: 65 * 10 }}
        />
      </div>
      <Modal
        title="크레딧 충전"
        open={isModalVisible}
        onOk={handleRecharge}
        onCancel={handleCancel}
        loading={isLoading}
      >
        <Space direction="vertical" size="middle">
          <Input
            style={{ marginTop: '16px' }}
            type="number"
            value={rechargeAmount}
            min={0}
            placeholder="충전할 크레딧"
            onChange={(e) => setRechargeAmount(e.target.value)}
          />
          <Radio.Group value={rechargeType} onChange={(e) => setRechargeType(e.target.value)}>
            <Space direction="vertical">
              <Radio value={'IMMEDIATE' satisfies RechargeType}>즉시 충전하기</Radio>
              <Radio value={'SCHEDULED' satisfies RechargeType}>충전 예약하기</Radio>
            </Space>
          </Radio.Group>
          {rechargeType === 'SCHEDULED' ? (
            <DatePicker
              showTime
              onChange={(value, dateString) => {
                console.log('Selected Time: ', value)
                console.log('Formatted Selected Time: ', dateString)
                handleScheduleChanged(value)
              }}
              // onOk={handleScheduleChanged}
              value={scheduleDate}
            />
          ) : null}
          {modalMessage ? <Typography.Text type="danger">{modalMessage}</Typography.Text> : null}
        </Space>
      </Modal>
    </>
  )
}
